import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Clock } from "lucide-react";

const WARN_MS = 120000; // 2-minute warning countdown
const EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];

/** Shows an "Are you still there?" modal after `minutes` of inactivity, with a
 *  2-minute countdown; logs out at 0. Clicking "Yes" dismisses and resets. */
export default function IdleLogout({ minutes, onLogout, enabled = true, accent }) {
  const [warning, setWarning] = useState(false);
  const [left, setLeft] = useState(WARN_MS);
  const idleTimer = useRef();
  const tick = useRef();
  const warningRef = useRef(false);
  warningRef.current = warning;
  let testSec = 0;
  try { testSec = parseInt(localStorage.getItem("lx_idle_test_sec") || "0", 10) * 1000; } catch (e) { /* noop */ }
  const idleMs = testSec > 0 ? testSec : Math.max(1000, minutes * 60000 - WARN_MS);

  const clearAll = () => { clearTimeout(idleTimer.current); clearInterval(tick.current); };

  const startWarning = useCallback(() => {
    setWarning(true); setLeft(WARN_MS);
    const end = Date.now() + WARN_MS;
    clearInterval(tick.current);
    tick.current = setInterval(() => {
      const rem = end - Date.now();
      setLeft(rem > 0 ? rem : 0);
      if (rem <= 0) { clearInterval(tick.current); onLogout && onLogout(); }
    }, 250);
  }, [onLogout]);

  const reset = useCallback(() => {
    clearAll(); setWarning(false);
    idleTimer.current = setTimeout(startWarning, idleMs);
  }, [idleMs, startWarning]);

  useEffect(() => {
    if (!enabled) { clearAll(); setWarning(false); return; }
    const onActivity = () => { if (!warningRef.current) reset(); };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    reset();
    return () => { EVENTS.forEach((e) => window.removeEventListener(e, onActivity)); clearAll(); };
  }, [enabled, reset]);

  if (!enabled || !warning) return null;
  const mm = Math.floor(left / 60000), ss = Math.floor((left % 60000) / 1000);
  const clock = left >= 60000 ? `${mm}:${String(ss).padStart(2, "0")}` : `0:${String(ss).padStart(2, "0")}`;
  const unitText = left >= 60000 ? `${mm}:${String(ss).padStart(2, "0")} minutes` : `${ss} seconds`;
  const a = accent || "#3a5a40";
  return createPortal(
    <div className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="idle-warning">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl app-bg text-foreground ring-1 ring-black/10 dark:ring-white/10 shadow-2xl p-6 text-center animate-[sheetUp_.28s_cubic-bezier(.22,1,.36,1)]">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${a}1f`, color: a }}><Clock size={26} /></div>
        <h3 className="font-display font-semibold text-[18px] mb-1">Are you still there?</h3>
        <p className="text-[13.5px] text-muted-foreground mb-3">You will be logged out in</p>
        <div className="text-[34px] font-display font-semibold tabular-nums leading-none mb-1" style={{ color: a }} data-testid="idle-countdown">{clock}</div>
        <p className="text-[12px] text-muted-foreground mb-5">{unitText} remaining</p>
        <button onClick={reset} data-testid="idle-stay" className="w-full h-11 rounded-xl text-white font-semibold lift" style={{ background: a }}>Yes, I'm still here</button>
      </div>
    </div>, document.body);
}
